import { uniqueId } from 'lodash-es';
import { GlButton, GlForm, GlFormInput, GlFormTextarea, GlAlert, GlSprintf, GlLink } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { helpPagePath } from '~/helpers/help_page_helper';
import { AI_CATALOG_TYPE_FLOW, MAX_LENGTH_NAME, MAX_LENGTH_DESCRIPTION, VISIBILITY_LEVEL_PRIVATE, VISIBILITY_LEVEL_RESTRICTED, VISIBILITY_LEVEL_PUBLIC, FLOW_VISIBILITY_LEVEL_DESCRIPTIONS, DEFAULT_FLOW_YML_STRING } from 'ee/ai/catalog/constants';
import { TYPENAME_PROJECT } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { __, s__ } from '~/locale';
import ErrorsAlert from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/errors_alert.vue?vue3';
import { AI_CATALOG_FLOWS_ROUTE, AI_CATALOG_FLOWS_SHOW_ROUTE } from '../router/constants';
import { getCancelRoute, getSubmitButtonText } from '../utils';
import AiCatalogFormButtons from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/ai_catalog_form_buttons.vue?vue3';
import FormFlowDefinition from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_flow_definition.vue?vue3';
import FormProjectDropdown from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_project_dropdown.vue?vue3';
import FormGroup from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_group.vue?vue3';
import FormSection from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_section.vue?vue3';
import VisibilityLevelRadioGroup from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/visibility_level_radio_group.vue?vue3';
export default {
    name: 'AiCatalogFlowForm',
    components: {
        ErrorsAlert,
        AiCatalogFormButtons,
        FormFlowDefinition,
        FormProjectDropdown,
        GlButton,
        GlForm,
        GlFormInput,
        GlFormTextarea,
        GlAlert,
        GlSprintf,
        GlLink,
        FormSection,
        FormGroup,
        VisibilityLevelRadioGroup
    },
    inject: {
        projectId: {
            default: null
        },
        isGlobalNamespace: {}
    },
    props: {
        mode: {
            type: String,
            required: true,
            validator: (mode)=>[
                    'edit',
                    'create'
                ].includes(mode)
        },
        isLoading: {
            type: Boolean,
            required: true
        },
        errors: {
            type: Array,
            required: true
        },
        initialValues: {
            type: Object,
            required: false,
            default () {
                return {
                    projectId: null,
                    name: '',
                    description: '',
                    definition: DEFAULT_FLOW_YML_STRING,
                    visibility: VISIBILITY_LEVEL_PRIVATE
                };
            }
        }
    },
    emits: [
        'dismiss-errors',
        'submit'
    ],
    data () {
        const projectId = !this.isGlobalNamespace && this.projectId ? convertToGraphQLId(TYPENAME_PROJECT, this.projectId) : this.initialValues.projectId;
        return {
            formValues: {
                ...this.initialValues,
                projectId,
                visibilityLevel: this.initialValues.visibility
            },
            formErrors: []
        };
    },
    computed: {
        formId () {
            return uniqueId('ai-catalog-flow-form-');
        },
        isEditMode () {
            return this.mode === 'edit';
        },
        allErrors () {
            return [
                ...this.errors,
                ...this.formErrors
            ];
        },
        submitButtonText () {
            return getSubmitButtonText(this.isEditMode, s__('AICatalog|Create flow'));
        },
        cancelRoute () {
            return getCancelRoute(this.$route.params, AI_CATALOG_FLOWS_SHOW_ROUTE, AI_CATALOG_FLOWS_ROUTE);
        }
    },
    watch: {
        'formValues.projectId': function validateProjectField() {
            this.$nextTick(()=>{
                this.$refs.fieldProject?.validate();
            });
        }
    },
    methods: {
        handleSubmit () {
            const isFormValid = this.validate();
            if (!isFormValid) {
                return;
            }
            const transformedValues = {
                projectId: this.isEditMode ? undefined : this.formValues.projectId,
                name: this.formValues.name.trim(),
                description: this.formValues.description.trim(),
                definition: this.formValues.definition.trim(),
                visibility: this.formValues.visibilityLevel
            };
            this.$emit('submit', transformedValues);
        },
        onError (error) {
            this.formErrors.push(error);
        },
        dismissErrors () {
            this.formErrors = [];
            this.$emit('dismiss-errors');
        },
        validate () {
            return Object.keys(this.$refs).filter((key)=>key.startsWith('field')).reduce((allValid, key)=>{
                const isFieldValid = this.$refs[key].validate();
                return allValid && isFieldValid;
            }, true);
        }
    },
    AI_CATALOG_TYPE_FLOW,
    VISIBILITY_LEVEL_PUBLIC,
    visibilityLevelTexts: {
        textPrivate: FLOW_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_PRIVATE],
        textRestricted: FLOW_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_RESTRICTED],
        textPublic: FLOW_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_PUBLIC]
    },
    fields: {
        projectId: {
            id: 'flow-form-project-id',
            label: s__('AICatalog|Managed by'),
            validations: {
                requiredLabel: s__('AICatalog|Project is required.')
            },
            groupAttrs: {
                labelDescription: s__('AICatalog|Only members of this project can edit or delete this flow.')
            }
        },
        name: {
            id: 'flow-form-name',
            label: __('Display name'),
            validations: {
                requiredLabel: s__('AICatalog|Name is required.'),
                maxLength: MAX_LENGTH_NAME
            },
            inputAttrs: {
                'data-testid': 'flow-form-input-name',
                placeholder: s__('AICatalog|Triage Issue, Generate Release Notes, Update Docs')
            }
        },
        description: {
            id: 'flow-form-description',
            label: __('Description'),
            validations: {
                requiredLabel: s__('AICatalog|Description is required.'),
                maxLength: MAX_LENGTH_DESCRIPTION
            }
        },
        visibilityLevel: {
            id: 'flow-form-visibility-level',
            label: __('Visibility'),
            groupAttrs: {
                labelDescription: s__('AICatalog|Choose who can view and interact with this flow.')
            }
        },
        definition: {
            id: 'flow-form-configuration',
            label: s__('AICatalog|YAML configuration'),
            validations: {
                requiredLabel: s__('AICatalog|Configuration is required.')
            },
            groupAttrs: {
                labelDescription: s__('AICatalog|This YAML configuration file determines the prompts, tools, and capabilities of your flow. Required properties: version, environment, components, prompts, routers, flow.')
            }
        }
    },
    publicDeletionHelpLink: helpPagePath('user/duo_agent_platform/flows/custom', {
        anchor: 'flow-visibility'
    })
};
